import { FormItem, Input, Label, Text, TextArea } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import { DitaQueryDetailsFragment, IssueType, useUpdateDitaQueryMutation } from 'generated/graphql';
import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateDitaQueryStore } from 'stores/UpdateDitaQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';

import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export interface DisplayQueryDetailsDitaProps {
  queryId: string;
  queryDetails: DitaQueryDetailsFragment;
}

export const DisplayQueryDetailsDita = forwardRef<SpecificQueryType, DisplayQueryDetailsDitaProps>(
  (props: DisplayQueryDetailsDitaProps, ref) => {
    const { queryDetails, queryId } = props;
    const { t } = useTranslation();
    const ditaQueryStore = useUpdateDitaQueryStore();
    const baseQueryStore = useUpdateBaseQueryStore();
    const { editMode, setCancelEditing } = useEditQueryStore();
    const [, callUpdateQuery] = useUpdateDitaQueryMutation();
    const classes = useStyles();

    useImperativeHandle(ref, () => ({
      cancel: () => {
        ditaQueryStore.setInitialStateWithProps(queryDetails);
      },
      save: () => {
        return saveQuery();
      },
      isValid: () => {
        return validateAllFields();
      }
    }));

    useEffect(() => {
      ditaQueryStore.setInitialStateWithProps(queryDetails);
    }, []);

    const validateAllFields = () => {
      const isFileTitleValid = validateFileTitle();
      const isLoioValid = validateLoio();
      const isFileNameValid = validateFileName();
      const isDeliverableValid = validateDeliverable();
      const isCmsProjectValid = validateCmsProject();
      const isSourceContactValid = validateSourceContact();

      return (
        isFileTitleValid &&
        isLoioValid &&
        isFileNameValid &&
        isDeliverableValid &&
        isCmsProjectValid &&
        isSourceContactValid
      );
    };

    const validateFileTitle = () => {
      return validateString(ditaQueryStore.fileTitle, ditaQueryStore.setFileTitleValueState);
    };

    const validateFileName = () => {
      return validateString(ditaQueryStore.fileName, ditaQueryStore.setFileNameValueState);
    };

    const validateLoio = () => {
      return validateString(ditaQueryStore.loio, ditaQueryStore.setLoioValueState);
    };

    const validateDeliverable = () => {
      return validateString(ditaQueryStore.deliverable, ditaQueryStore.setDeliverableValueState);
    };

    const validateCmsProject = () => {
      return validateString(ditaQueryStore.cmsProject, ditaQueryStore.setCmsProjectValueState);
    };

    const validateSourceContact = () => {
      return validateString(
        ditaQueryStore.sourceContact,
        ditaQueryStore.setSourceContactValueState
      );
    };

    const onChangeXtmProjectName = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      ditaQueryStore.setXtmProjectName(value);
    };

    const onChangeFileTitle = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      ditaQueryStore.setFileTitle(value);
      validateString(value, ditaQueryStore.setFileTitleValueState);
    };

    const onChangeFileName = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      ditaQueryStore.setFileName(value);
      validateString(value, ditaQueryStore.setFileNameValueState);
    };

    const onChangeLoio = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLTextAreaElement;
      ditaQueryStore.setLoio(value);
      validateString(value, ditaQueryStore.setLoioValueState);
    };

    const onChangeDeliverable = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLTextAreaElement;
      ditaQueryStore.setDeliverable(value);
      validateString(value, ditaQueryStore.setDeliverableValueState);
    };

    const onChangeCmsProject = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLTextAreaElement;
      ditaQueryStore.setCmsProject(value);
      validateString(value, ditaQueryStore.setCmsProjectValueState);
    };

    const onChangeSourceContact = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      ditaQueryStore.setSourceContact(value);
      validateString(value, ditaQueryStore.setSourceContactValueState);
    };

    const onChangeSubmap = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      ditaQueryStore.setSubmap(value);
    };

    const saveQuery = async () => {
      const result = await callUpdateQuery({
        input: {
          id: Number(queryId),
          sourceText: baseQueryStore.sourceText,
          targetText: baseQueryStore.targetText,
          deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
          targetLanguage: baseQueryStore.targetLanguage,
          issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
          details: baseQueryStore.details,
          ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
          distributions: baseQueryStore.distributions,
          resetedDistributions: baseQueryStore.resetedDistributions,
          fileTitle: ditaQueryStore.fileTitle,
          fileName: ditaQueryStore.fileName,
          loio: ditaQueryStore.loio,
          deliverable: ditaQueryStore.deliverable,
          cmsProject: ditaQueryStore.cmsProject,
          submap: ditaQueryStore.submap,
          xtmProjectName: ditaQueryStore.xtmProjectName,
          sourceContact: ditaQueryStore.sourceContact,
          qeReview: baseQueryStore.qeReview
        }
      });
      return { error: result.error };
    };

    const getDetailsData = (): FormDisplayItem[] => {
      return [
        {
          renderComponent: () => (
            <TextArea
              value={ditaQueryStore.cmsProject}
              data-testid="edit-cms-project"
              data-help-id="edit-cms-project"
              valueState={ditaQueryStore.cmsProjectValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-cms-project')}
                </Label>
              }
              onChange={onChangeCmsProject}
            />
          ),
          dataTestId: 'cms-project',
          data: ditaQueryStore.cmsProject,
          i18nTag: t('display-query-details-dita-cms-project'),
          required: true
        },
        {
          renderComponent: () => (
            <TextArea
              value={ditaQueryStore.deliverable}
              data-testid="edit-deliverable"
              data-help-id="edit-deliverable"
              valueState={ditaQueryStore.deliverableValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-deliverable')}
                </Label>
              }
              onChange={onChangeDeliverable}
            />
          ),
          dataTestId: 'deliverable',
          data: ditaQueryStore.deliverable,
          i18nTag: t('display-query-details-dita-deliverable'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={ditaQueryStore.fileName}
              data-testid="edit-file-name"
              data-help-id="edit-file-name"
              className={classes.formLineInput}
              valueState={ditaQueryStore.fileNameValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-file-name')}
                </Label>
              }
              onChange={onChangeFileName}
            />
          ),
          dataTestId: 'file-name',
          data: ditaQueryStore.fileName,
          i18nTag: t('display-query-details-dita-file-name'),
          required: true
        },
        {
          renderComponent: () => (
            <TextArea
              value={ditaQueryStore.loio}
              data-testid="edit-loio"
              data-help-id="edit-loio"
              valueState={ditaQueryStore.loioValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-loio')}
                </Label>
              }
              onChange={onChangeLoio}
            />
          ),
          dataTestId: 'loio',
          data: ditaQueryStore.loio,
          i18nTag: t('display-query-details-dita-loio'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={ditaQueryStore.fileTitle}
              className={classes.formLineInput}
              data-testid="edit-file-title"
              data-help-id="edit-file-title"
              valueState={ditaQueryStore.fileTitleValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-file-title')}
                </Label>
              }
              onChange={onChangeFileTitle}
            />
          ),
          dataTestId: 'file-title',
          data: ditaQueryStore.fileTitle,
          i18nTag: t('display-query-details-dita-file-title'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={ditaQueryStore.xtmProjectName}
              data-testid="edit-xtm-project-name"
              data-help-id="edit-xtm-project-name"
              className={classes.formLineInput}
              onChange={onChangeXtmProjectName}
            />
          ),
          dataTestId: 'xtm-project-name',
          data: ditaQueryStore.xtmProjectName,
          i18nTag: t('display-query-details-dita-xtm-project-name')
        },
        {
          renderComponent: () => (
            <Input
              value={ditaQueryStore.sourceContact}
              data-testid="edit-source-contact"
              data-help-id="edit-source-contact"
              className={classes.formLineInput}
              valueState={ditaQueryStore.sourceContactValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-source-contact')}
                </Label>
              }
              onChange={onChangeSourceContact}
            />
          ),
          dataTestId: 'source-contact',
          data: queryDetails.sourceContactDisplayName,
          i18nTag: t('display-query-details-dita-source-contact'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={ditaQueryStore.submap}
              data-testid="edit-submap"
              data-help-id="edit-submap"
              className={classes.formLineInput}
              onChange={onChangeSubmap}
            />
          ),
          dataTestId: 'submap',
          data: ditaQueryStore.submap,
          i18nTag: t('display-query-details-dita-submap')
        }
      ];
    };

    let queryDetailsData: FormDisplayItem[] = getDetailsData();

    useEffect(() => {
      queryDetailsData = getDetailsData();
    }, [queryDetails]);

    return (
      <>
        {queryDetailsData.map((entry) => (
          <FormItem
            key={entry.dataTestId}
            labelContent={
              <Label
                id={entry.i18nTag}
                required={editMode && entry.required}
                data-help-id={entry.dataTestId}
                className={classes.formItemLabel}
                wrappingType="None"
              >
                {entry.i18nTag}
              </Label>
            }
          >
            {editMode && entry.editable !== false ? (
              entry.renderComponent()
            ) : (
              <Text
                data-testid={entry.dataTestId}
                aria-labelledby={entry.i18nTag}
                className={classes.formItemText}
              >
                {entry.data}
              </Text>
            )}
          </FormItem>
        ))}
      </>
    );
  }
);
